import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import List from '../../../../controls/List';
import Toolbar from '../../../../controls/Toolbar';
import AddLayoutItemModal from '../modals/AddLayoutItemModal';
import { useUIConfig } from '../Context';

const LayoutItemListRenderer = ({
    name,
    allowedGroups,
    description,
    label,
    path,
    initialValue,
    disabled,
    singleItem = false,
}) => {
    const { add, remove, edit, reorder, sortComponents } = useUIConfig();
    const [translate] = useTranslation();
    const [data, setData] = useState([]);

    const [addingComponentType, setAddingComponentType] = useState(null);
    const [hiddenComponentIndices, setHiddenComponentIndices] = useState([]);
    const [visibleComponentIndices, setVisibleComponentIndices] = useState([]);

    const addLayoutItem = useCallback(({ path, layoutItem, }) => {
        if (singleItem) {
            add(path, {
                name: layoutItem,
            }, true);
        }
        else {
            add(path, {
                LayoutItem: {
                    name: layoutItem,
                },
            });
        }

        setAddingComponentType(null);
    }, [add, singleItem]);

    const reorderLayoutItems = useCallback(({ path, oldIndex, newIndex, }) => {
        reorder(path, oldIndex, newIndex);
    }, [reorder]);

    const callbacks = useCallback(() => {
        if (disabled) {
            return null;
        }

        const result = {
            Delete: {
                hiddenIndices: hiddenComponentIndices,
                icon: 'Delete',
                variant: 'outline-danger',
                callback: (_event, key) => {
                    if (singleItem) {
                        remove(path.slice(0, -1), path.at(-1), true);
                    }
                    else {
                        remove(path, key);
                    }
                },
            },
        };

        if (!singleItem) {
            result.ReAdd = {
                hiddenIndices: visibleComponentIndices,
                icon: 'Add',
                variant: 'outline-primary',
                callback: (_event, key) => edit([...path, key], 'Hidden', false),
            };
        }

        return result;
    }, [disabled, edit, hiddenComponentIndices, visibleComponentIndices, path, remove, singleItem]);

    useEffect(() => {
        if (!initialValue) {
            setHiddenComponentIndices([]);
            setVisibleComponentIndices([]);
            setData([]);
            return;
        }

        const hiddenIndices = [];
        const visibleIndices = [];

        if (singleItem) {
            if (initialValue?.config?.Hidden) {
                hiddenIndices.push(0);
            }
            else {
                visibleIndices.push(0);
            }

            const itemName = initialValue?.name;
            setData(itemName ? [{
                name: itemName,
            }] : []);
        }
        else {
            const arr = sortComponents(initialValue).map(([configKey, config], i) => {
                if (config?.Hidden) {
                    hiddenIndices.push(i);
                }
                else {
                    visibleIndices.push(i);
                }

                return {
                    _id: configKey,
                    name: config?.LayoutItem?.name,
                };
            });
            setData(arr);
        }

        setHiddenComponentIndices(hiddenIndices);
        setVisibleComponentIndices(visibleIndices);
    }, [initialValue, sortComponents, singleItem]);

    const renderToolbar = useMemo(() =>
        <div className="border-accenture border-bottom">
            <Toolbar
                buttons={(disabled || (singleItem && data.length > 0)) ? [] : [
                    {
                        name: 'Add',
                        onClick: () => setAddingComponentType(name)
                    }
                ]}
                heading={label}
                tooltip={description}
                buttonTooltip={false}
                className='h6'
            />
        </div>, [disabled, label, name, description, singleItem, data.length]);

    return (
        <>
            {renderToolbar}
            <AddLayoutItemModal
                show={addingComponentType === name}
                allowedGroups={allowedGroups}
                onAdd={({ layoutItem }) => addLayoutItem({ path, layoutItem })}
                onCancel={() => setAddingComponentType(null)}
            />
            {data.length > 0 ? <List
                data={data}
                attr='name'
                listItemCss='p-1 ps-3'
                selectedElement={null}
                disabled={disabled}
                callbacks={disabled ? null : callbacks()}
                drag={(disabled || singleItem) ? null : {
                    type: 'ComponentListItem',
                    onReorder: (dragIndex, hoverIndex) => reorderLayoutItems({ path, oldIndex: dragIndex, newIndex: hoverIndex })
                }}
            /> :
                <label className={`p-1 ps-3 w-100 border border-1 border-top-0 ${disabled ? 'disabled-background' : 'bg-body'}`}> {translate("OTM_NO_DATA")}</label >}
        </>
    );
};

export default LayoutItemListRenderer;